// Lightweight portal popover. Renders its panel into document.body with fixed
// positioning anchored to the trigger, so it escapes the table's overflow-x-auto
// clipping (unlike an absolutely-positioned dropdown). Closes on outside click,
// Escape, and repositions on scroll/resize. No extra dependency (we only have
// @radix-ui/react-dialog in the tree).

import type { JSX, ReactNode } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

export function Popover({
  trigger,
  children,
  align = "start",
  className,
}: {
  trigger: (o: { open: boolean; toggle: () => void }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: "start" | "end";
  className?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number }>({ top: -9999 });

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(
      align === "end"
        ? { top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) }
        : { top: r.bottom + 4, left: r.left },
    );
  }, [align]);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onMove = (): void => {
      place();
    };
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = useCallback(() => {
    setOpen((v) => !v);
  }, []);
  const close = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <span ref={anchorRef} className="inline-flex">
      {trigger({ open, toggle })}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{ position: "fixed", top: pos.top, left: pos.left, right: pos.right, zIndex: 60 }}
            className={cn(
              "bg-popover text-popover-foreground border-border min-w-[11rem] rounded-md border p-1 shadow-md",
              className,
            )}
          >
            {children(close)}
          </div>,
          document.body,
        )}
    </span>
  );
}
